import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Alert, Button, Field, Wordmark } from '@evogard/ui';
import { MIN_PASSWORD_LENGTH } from '@evogard/contract';
import { api, errorMessage, ok } from '../../lib/api.js';
import { CONTEXT_KEY } from '../../lib/session.js';
import { auth } from '../../lib/auth.js';

/**
 * O primeiro acesso do COLABORADOR, pelo link do convite — `plataforma-10`.
 *
 * ## O que esta tela não faz
 *
 * **Não confirma cadastro.** O link inválido diz que é inválido e manda pedir
 * outro — não diz "este convite expirou em 12/08", porque a API não distingue
 * expirado de já aceito de inexistente. Se a tela fosse mais específica que o
 * servidor, o oráculo que a resposta única fechou voltaria pelo cliente. E o
 * que ele entregaria aqui é a lista de quem trabalha na casa.
 *
 * **Não valida senha por conta própria além do que o backend exige.** O mínimo
 * é o do contrato, que é o mesmo do `auth.ts`; repetir a regra com outro número
 * é como os dois passam a discordar.
 *
 * ## Gêmea da do portal, e separada por construção
 *
 * A do associado mora em `apps/portal`. São duas telas porque são duas
 * aplicações, dois domínios e duas sessões (regra 10) — e a diferença visível é
 * a moldura: aqui a marca diz "Plataforma", como na tela de entrar.
 */
export function FirstAccessScreen() {
  const { t: token } = useSearch({ from: '/first-access' });
  const navigate = useNavigate();
  const client = useQueryClient();

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);

  const invite = useQuery({
    queryKey: ['plataforma', 'convite', token],
    enabled: Boolean(token),
    retry: false,
    queryFn: async () => {
      const r = await api.platform.validateFirstAccess({ body: { token: token! } });
      return ok(r, 200, 'Este link não vale mais.');
    },
  });

  const complete = useMutation({
    mutationFn: async () => {
      const r = await api.platform.completeFirstAccess({
        body: { token: token!, password },
      });
      if (r.status !== 200) {
        throw new Error(errorMessage(r.body, 'Não foi possível criar a senha.'));
      }

      /*
       * Entra em seguida, com a senha que a pessoa acabou de escolher. Mandá-la
       * ao login para digitar de novo o que digitou há um segundo é a fricção
       * que faz desistir no último passo — e o e-mail vem da resposta, que só o
       * devolve depois de a senha estar gravada.
       *
       * Se este login falhar, o cadastro está feito de qualquer jeito: a tela
       * cai no `/sign-in` com a senha valendo. Por isso o erro daqui não
       * derruba o fluxo.
       */
      const signedIn = await auth.signIn
        .email({ email: r.body.email, password })
        .then((res) => !res.error)
        .catch(() => false);

      return { signedIn };
    },
    onSuccess: ({ signedIn }) => {
      void client.resetQueries({ queryKey: CONTEXT_KEY });
      void navigate({ to: signedIn ? '/' : '/sign-in', replace: true });
    },
    onError: (e: Error) => setError(e.message),
  });

  const passwordShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const notCheck = confirmation.length > 0 && password !== confirmation;

  function send(e: FormEvent) {
    e.preventDefault();
    setError(null);
    complete.mutate();
  }

  return (
    <div className="grid min-h-dvh place-items-center p-6">
      <div className="w-full max-w-sm">
        <div className="mb-6">
          <Wordmark size="capa" level="h1" subtitle="Plataforma" surface="superficie" />
          <p className="mt-3 text-sm text-texto-suave">
            {invite.data
              ? `Olá, ${invite.data.nome}. Crie a sua senha para entrar.`
              : 'Primeiro acesso.'}
          </p>
        </div>

        {/* sem token na URL: alguém colou o endereço sem a parte que importa */}
        {!token && (
          <Alert tone="alerta" title="Falta a parte final do link.">
            Abra o link do e-mail inteiro, sem cortar. Se ele não funcionar, peça um novo
            a quem administra a plataforma.
          </Alert>
        )}

        {token && invite.isLoading && <span className="esqueleto block h-10 w-full" />}

        {/*
         * A recusa vem do servidor, inteira. A tela não distingue expirado de
         * já aceito de inexistente — o servidor não distingue, e ser mais
         * específica aqui reabriria o que a resposta única fechou.
         */}
        {token && invite.isError && (
          <Alert tone="alerta" title="Este link não vale mais.">
            {invite.error.message} Links de primeiro acesso valem por poucos dias e só
            podem ser usados uma vez.
          </Alert>
        )}

        {invite.data && (
          <form className="flex flex-col gap-3" onSubmit={send}>
            {error && <Alert tone="erro">{error}</Alert>}

            <p className="text-sm text-texto-suave">
              O convite é para <strong>{invite.data.emailMasked}</strong>.
            </p>

            <Field
              label="Nova senha"
              type="password"
              required
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={passwordShort ? `Use pelo menos ${MIN_PASSWORD_LENGTH} caracteres.` : null}
              help={`Pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`}
            />

            <Field
              label="Repita a senha"
              type="password"
              required
              autoComplete="new-password"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              error={notCheck ? 'As duas não são iguais.' : null}
            />

            <Button
              type="submit"
              loading={complete.isPending}
              disabled={password.length < MIN_PASSWORD_LENGTH || password !== confirmation}
            >
              Criar senha e entrar
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
